<template>
  <div id="app" style="text-align: center; padding-top: 60px;">
    <!-- 微信内：加载中 -->
    <div v-if="isWechat && loading">
      <van-loading size="40px" vertical>微信授权中...</van-loading>
    </div>

    <!-- 微信内：错误 -->
    <div v-if="isWechat && error" style="color: #ff4444; padding: 20px;">
      <van-icon name="fail" size="60px" />
      <p style="margin-top: 20px; font-size: 16px;">{{ errorMsg }}</p>
      <van-button type="primary" style="margin-top: 30px;" @click="retryAuth">
        重新授权
      </van-button>
    </div>

    <!-- PC端：扫码登录 -->
    <div v-if="!isWechat" class="qrcode-container">
      <h2 style="font-size: 20px; color: #333; margin-bottom: 30px;">微信扫码登录</h2>

      <!-- 二维码 -->
      <div v-if="qrcodeStatus === 'loading'" style="padding: 40px;">
        <van-loading size="40px" vertical>生成二维码...</van-loading>
      </div>

      <div v-else-if="qrcodeStatus === 'pending'" class="qrcode-box">
        <div class="qrcode-image">
          <img v-if="qrcodeUrl" :src="qrcodeDataUrl" alt="扫码登录" style="width: 200px; height: 200px;">
        </div>
        <p style="color: #666; font-size: 14px; margin-top: 20px;">请使用微信扫一扫</p>
        <p style="color: #999; font-size: 12px; margin-top: 10px;">二维码 {{ expireCountdown }} 秒后过期</p>
      </div>

      <div v-else-if="qrcodeStatus === 'scanned'" class="qrcode-box">
        <van-icon name="success" size="60px" color="#07c160" />
        <p style="color: #07c160; font-size: 16px; margin-top: 20px;">已扫码，请在手机上确认</p>
      </div>

      <div v-else-if="qrcodeStatus === 'expired'" class="qrcode-box">
        <van-icon name="clock-o" size="60px" color="#999" />
        <p style="color: #999; font-size: 16px; margin-top: 20px;">二维码已过期</p>
        <van-button type="primary" style="margin-top: 20px;" @click="generateQrcode">
          刷新二维码
        </van-button>
      </div>

      <div v-else-if="qrcodeStatus === 'error'" class="qrcode-box">
        <van-icon name="fail" size="60px" color="#ff4444" />
        <p style="color: #ff4444; font-size: 16px; margin-top: 20px;">{{ errorMsg }}</p>
        <van-button type="primary" style="margin-top: 20px;" @click="generateQrcode">
          重试
        </van-button>
      </div>

      <!-- 微信内打开提示 -->
      <div style="margin-top: 40px; padding: 0 40px;">
        <p style="color: #999; font-size: 12px;">
          已在微信内？
          <a :href="wechatDirectUrl" style="color: #576b95;">直接授权登录</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { setToken, setUser, clearAuth } from '../utils/auth'
import common from '../assets/js/common'

export default {
  name: 'Auth',
  data() {
    return {
      // 微信内
      isWechat: false,
      loading: true,
      error: false,
      errorMsg: '',

      // PC端扫码
      qrcodeStatus: 'loading', // loading, pending, scanned, expired, success, error
      qrcodeUuid: '',
      qrcodeUrl: '',
      qrcodeDataUrl: '',
      expireCountdown: 300,
      pollTimer: null,
      countdownTimer: null,

      // 通用
      redirect: '/payment'
    }
  },
  computed: {
    // 微信内直接授权链接（供"已在微信内"链接使用）
    wechatDirectUrl() {
      const currentUrl = window.location.origin + '/auth?redirect=' + encodeURIComponent(this.redirect)
      return 'https://api.dongbiaodi.cc/bind_admin.php?redirect_url=' + encodeURIComponent(currentUrl)
    }
  },
  created() {
    this.initAuth()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    /**
     * 初始化认证
     */
    initAuth() {
      // 获取跳转目标
      this.redirect = this.$route.query.redirect || '/payment'

      // 检测是否在微信内
      this.isWechat = this.checkIsWechat()

      if (this.isWechat) {
        // 微信内：走静默授权流程
        this.handleWechatAuth()
      } else {
        // PC端：显示二维码
        this.generateQrcode()
      }
    },

    /**
     * 检测是否在微信浏览器内
     */
    checkIsWechat() {
      const ua = navigator.userAgent.toLowerCase()
      return /micromessenger/.test(ua)
    },

    /**
     * 微信内：处理授权流程
     */
    handleWechatAuth() {
      const urlParams = new URLSearchParams(window.location.search)
      const openid = urlParams.get('openid')

      if (openid && openid !== 'undefined' && openid !== '') {
        this.loginByOpenid(openid)
      } else {
        this.gotoWechatAuth()
      }
    },

    /**
     * 微信内：用openid换取token
     */
    loginByOpenid(openid) {
      common.commonHttp('/api/saas_api_noauth.php?action=login_by_openid', {
        openid: openid
      }).then((res) => {
        const data = res.data

        if (data.status === 1 && data.data) {
          setToken(data.data.token)
          setUser(data.data.user)

          // 尝试绑定邀请人
          const inviterId = localStorage.getItem('saas_inviter_id')
          if (inviterId) {
            common.cusHttp(this, '/api/saas_finance.php?action=bind_inviter', { inviter_id: inviterId }, 'post').catch(e => {})
          }

          this.$router.replace(this.redirect)
        } else {
          this.error = true
          this.errorMsg = data.msg || '登录失败'
          this.loading = false
        }
      }).catch((err) => {
        console.error('Login error:', err)
        this.error = true
        this.errorMsg = '网络错误，请重试'
        this.loading = false
      })
    },

    /**
     * 微信内：跳转到微信授权页
     */
    gotoWechatAuth() {
      const currentUrl = window.location.origin + '/auth?redirect=' + encodeURIComponent(this.redirect)
      const authUrl = 'https://api.dongbiaodi.cc/bind_admin.php?redirect_url=' + encodeURIComponent(currentUrl)

      window.location.href = authUrl
    },

    /**
     * 微信内：重试授权
     */
    retryAuth() {
      this.error = false
      this.loading = true
      clearAuth()
      this.gotoWechatAuth()
    },

    /**
     * PC端：生成二维码
     */
    generateQrcode() {
      this.clearTimers()
      this.qrcodeStatus = 'loading'
      this.expireCountdown = 300

      common.commonHttp('/api/saas_api_noauth.php?action=generate_qrcode', {})
        .then((res) => {
          if (res.data.status === 1 && res.data.data) {
            this.qrcodeUuid = res.data.data.uuid
            this.qrcodeUrl = res.data.data.qrcode_url
            this.expireCountdown = res.data.data.expire_seconds || 300

            // 生成二维码图片（使用qrcodejs API或Google Chart API）
            this.generateQrcodeImage(this.qrcodeUrl)

            this.qrcodeStatus = 'pending'

            // 开始轮询和倒计时
            this.startPolling()
            this.startCountdown()
          } else {
            this.qrcodeStatus = 'error'
            this.errorMsg = res.data.msg || '生成二维码失败'
          }
        })
        .catch((err) => {
          console.error('Generate qrcode error:', err)
          this.qrcodeStatus = 'error'
          this.errorMsg = '网络错误，请重试'
        })
    },

    /**
     * PC端：生成二维码图片
     */
    generateQrcodeImage(url) {
      // 使用Google Chart API生成二维码（无需引入额外库）
      // 也可以替换为其他二维码生成服务
      const qrSize = 200
      this.qrcodeDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(url)}`
    },

    /**
     * PC端：开始轮询登录状态
     */
    startPolling() {
      this.pollTimer = setInterval(() => {
        this.checkQrcodeStatus()
      }, 2000) // 每2秒轮询一次
    },

    /**
     * PC端：检查二维码状态
     */
    checkQrcodeStatus() {
      if (!this.qrcodeUuid) return

      common.commonHttp('/api/saas_api_noauth.php?action=check_qrcode_status', {
        uuid: this.qrcodeUuid
      }).then((res) => {
        if (res.data.status !== 1) return

        const data = res.data.data
        const status = data.status

        if (status === 1) {
          // 已扫码
          this.qrcodeStatus = 'scanned'
        } else if (status === 2) {
          // 已登录，获取token
          this.clearTimers()
          this.qrcodeStatus = 'success'

          if (data.token) {
            setToken(data.token)
            setUser(data.user_info)
            
            // 尝试绑定邀请人
            const inviterId = localStorage.getItem('saas_inviter_id')
            if (inviterId) {
              common.cusHttp(this, '/api/saas_finance.php?action=bind_inviter', { inviter_id: inviterId }, 'post').catch(e => {})
            }

            this.$router.replace(this.redirect)
          }
        } else if (status === 3) {
          // 已过期
          this.clearTimers()
          this.qrcodeStatus = 'expired'
        }
      }).catch((err) => {
        console.error('Poll error:', err)
      })
    },

    /**
     * PC端：倒计时
     */
    startCountdown() {
      this.countdownTimer = setInterval(() => {
        this.expireCountdown--
        if (this.expireCountdown <= 0) {
          this.clearTimers()
          this.qrcodeStatus = 'expired'
        }
      }, 1000)
    },

    /**
     * 清理定时器
     */
    clearTimers() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    }
  }
}
</script>

<style>
html, body {
  background: #f5f5f5 !important;
}

.qrcode-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
}

.qrcode-box {
  background: white;
  border-radius: 8px;
  padding: 40px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.qrcode-image {
  width: 200px;
  height: 200px;
  margin: 0 auto;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-image img {
  display: block;
}
</style>
